'use client';

import React__default, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { CombinePanelContainer, CombinePanelWrap, CombinePanelColumn, CombinePanelItem, CombinePanelButtonGroup } from '../../styles.css.js';
import { ELanguage } from '../../../Utils/constants.js';
import { months, FIRST_DAY, LAST_DAY, FIRST_MONTH } from '../../const.js';
import { useCalendar } from '../../context/calendar.js';
import { getDate, getMonth, getFullYear } from '../../utils/calendar.js';
import Button from '../../../Button/index.js';
import { ECalendarType } from '../../types.js';

var DELAY_TIME_TO_DETECT_DOM_MOUNTED = 100;
var behaviorOption = { behavior: 'smooth', block: 'center', inline: 'center' };
function CombinePanel() {
    var _a = useCalendar(), currentDate = _a.currentDate, langCode = _a.langCode, rangeYears = _a.rangeYears, handleDatesClick = _a.handleDatesClick, minDate = _a.minDate, maxDate = _a.maxDate, calendarType = _a.calendarType;
    var _b = useState(false), isMounted = _b[0], setIsMounted = _b[1];
    var _c = useState(function () { var _a; return (_a = getDate(currentDate)) !== null && _a !== void 0 ? _a : 1; }), day = _c[0], setDay = _c[1];
    var _d = useState(function () { var _a; return (_a = getMonth(currentDate)) !== null && _a !== void 0 ? _a : 0; }), month = _d[0], setMonth = _d[1];
    var _e = useState(function () { return getFullYear(currentDate !== null && currentDate !== void 0 ? currentDate : new Date()); }), year = _e[0], setYear = _e[1];
    var timer = useRef();
    var dayActiveRef = useRef();
    var monthActiveRef = useRef();
    var yearActiveRef = useRef();
    var memoRangeYear = useMemo(function () {
        var _a, _b;
        var startYear = (_a = rangeYears === null || rangeYears === void 0 ? void 0 : rangeYears[0]) !== null && _a !== void 0 ? _a : 1900;
        var endYear = (_b = rangeYears === null || rangeYears === void 0 ? void 0 : rangeYears[1]) !== null && _b !== void 0 ? _b : new Date().getFullYear();
        return Array.from({ length: endYear - (startYear - 1) }, function (_, a) { return a + startYear; });
    }, [rangeYears]);
    var memoDays = useMemo(function () {
        if (calendarType === ECalendarType.combineMonthAndYear)
            return;
        var lastDayOfThisMonth = new Date(year, month + 1, LAST_DAY);
        var numberOfdaysInMonth = getDate(lastDayOfThisMonth);
        var days = [];
        for (var i = 1; i <= numberOfdaysInMonth; i++) {
            var day_1 = new Date(year, month, i);
            days.push(getDate(day_1));
        }
        if (day && !days.includes(day)) {
            setDay(null);
        }
        return days;
    }, [day, year, month, calendarType]);
    var handleClickDay = function (value) {
        setDay(value);
    };
    var handleClickMonth = function (value) {
        setMonth(value);
    };
    var handleClickYear = function (value) {
        setYear(value);
    };
    var handleApprove = useCallback(function () {
        if (typeof day !== 'number')
            return;
        var newDate = new Date(year, month, day);
        handleDatesClick(newDate);
    }, [day, month, year, handleDatesClick]);
    var handleCancel = useCallback(function () {
        var _a, _b;
        handleDatesClick(currentDate);
        setDay((_a = getDate(currentDate)) !== null && _a !== void 0 ? _a : 1);
        setMonth((_b = getMonth(currentDate)) !== null && _b !== void 0 ? _b : 0);
        setYear(getFullYear(currentDate !== null && currentDate !== void 0 ? currentDate : new Date()));
    }, [currentDate, handleDatesClick]);
    useEffect(function () {
        if (timer.current) {
            return;
        }
        if (!isMounted) {
            setIsMounted(true);
            return;
        }
        if (isMounted) {
            timer.current = setTimeout(function () {
                dayActiveRef.current && dayActiveRef.current.scrollIntoView(behaviorOption);
                monthActiveRef.current && monthActiveRef.current.scrollIntoView(behaviorOption);
                yearActiveRef.current && yearActiveRef.current.scrollIntoView(behaviorOption);
            }, DELAY_TIME_TO_DETECT_DOM_MOUNTED);
        }
        return function () {
            dayActiveRef.current = undefined;
            monthActiveRef.current = undefined;
            yearActiveRef.current = undefined;
            clearTimeout(timer.current);
            setIsMounted(false);
        };
    }, [isMounted]);
    return (React__default.createElement(CombinePanelContainer, null,
        React__default.createElement(CombinePanelWrap, { height: 270 },
            calendarType !== ECalendarType.combineMonthAndYear ? (React__default.createElement(CombinePanelColumn, null, memoDays === null || memoDays === void 0 ? void 0 : memoDays.map(function (d, i) {
                var isActive = i + 1 === day;
                var newDate = new Date(year, month !== null && month !== void 0 ? month : FIRST_MONTH, d);
                var disabled = (maxDate && newDate > maxDate) || (minDate && newDate < minDate);
                return (React__default.createElement(CombinePanelItem, { ref: function (el) {
                        if (isActive && el) {
                            dayActiveRef.current = el;
                        }
                    }, key: d, "data-active": !!day, type: isActive ? 'highlight' : 'transparent', onClick: function () { return handleClickDay(d); }, disabled: disabled }, d));
            }))) : null,
            React__default.createElement(CombinePanelColumn, null, months === null || months === void 0 ? void 0 : months.map(function (m) {
                var isActive = m.id === month;
                var newDate = new Date(year, m.id, day !== null && day !== void 0 ? day : FIRST_DAY);
                var disabled = (maxDate && newDate > maxDate) || (minDate && newDate < minDate);
                return (React__default.createElement(CombinePanelItem, { ref: function (el) {
                        if (isActive && el) {
                            monthActiveRef.current = el;
                        }
                    }, key: m.id, type: isActive ? 'highlight' : 'transparent', onClick: function () { return handleClickMonth(m.id); }, disabled: disabled }, langCode === ELanguage.VI ? m.name : m.sortNameEn));
            })),
            React__default.createElement(CombinePanelColumn, null, memoRangeYear === null || memoRangeYear === void 0 ? void 0 : memoRangeYear.map(function (y) {
                var isActive = y === year;
                var newDate = new Date(y, month !== null && month !== void 0 ? month : FIRST_MONTH, day !== null && day !== void 0 ? day : FIRST_DAY);
                var disabled = (maxDate && newDate > maxDate) || (minDate && newDate < minDate);
                return (React__default.createElement(CombinePanelItem, { ref: function (el) {
                        if (isActive && el) {
                            yearActiveRef.current = el;
                        }
                    }, key: y, type: isActive ? 'highlight' : 'transparent', onClick: function () { return handleClickYear(y); }, disabled: disabled }, y));
            }))),
        React__default.createElement(CombinePanelButtonGroup, null,
            React__default.createElement(Button, { type: 'default', onClick: handleCancel }, langCode === ELanguage.VI ? 'Huỷ' : 'Cancel'),
            React__default.createElement(Button, { type: 'primary', onClick: handleApprove, disabled: typeof day !== 'number' }, langCode === ELanguage.VI ? 'Chọn' : 'Done'))));
}

export { CombinePanel as default };
//# sourceMappingURL=index.js.map
